import { ConnectError } from '@connectrpc/connect'
import { FetchError } from '@repo/rpc-codegen/fetch-rpc'
import { isAllAccessRequiredError } from '@web/hooks/use-all-access-guard'

const PENDING_COIN_PAYMENT_INTENT_STATUSES = new Set([
  'COIN_PAYMENT_INTENT_STATUS_PROCESSING',
  'COIN_PAYMENT_INTENT_STATUS_REQUIRES_ACTION',
])

const PROMPT_MODERATION_DESCRIPTIONS = new Set(['prompt_contains_sexual_minors'])

interface CoinErrorDetails {
  code?: string
  insufficientFundDetails?: { requiredBalance?: string | number }
  paymentIntentResult?: { stripeInvoicePayUrl?: string; status?: string }
}

export type MarketplaceToolErrorAction =
  | { type: 'studio-ai' }
  | { type: 'all-access' }
  | { type: 'payment-required'; payUrl: string | null; status?: string }
  | { type: 'insufficient-funds'; requiredBalance: number | null }
  | { type: 'nsfw' }
  | { type: 'upload-minor' }
  | { type: 'prompt-minor' }
  | { type: 'generic' }

export function classifyMarketplaceToolError(error: unknown): MarketplaceToolErrorAction {
  const debug = getDebug(error)
  const coinError = getCoinError(debug)
  const paymentStatus = coinError?.paymentIntentResult?.status

  // The page already ran the All Access guard. This coin code means the account
  // has All Access but not Studio AI, so it must not reopen the All Access paywall.
  if (coinError?.code === 'COIN_ERROR_CODE_MISSING_AI_STUDIO_SUBSCRIPTION') {
    return { type: 'studio-ai' }
  }

  if (mentionsAllAccess(error, debug)) {
    return { type: 'all-access' }
  }

  if (paymentStatus && PENDING_COIN_PAYMENT_INTENT_STATUSES.has(paymentStatus)) {
    return {
      type: 'payment-required',
      payUrl: coinError?.paymentIntentResult?.stripeInvoicePayUrl ?? null,
      status: paymentStatus,
    }
  }

  if (coinError?.code === 'COIN_ERROR_CODE_INSUFFICIENT_FUNDS') {
    return { type: 'insufficient-funds', requiredBalance: getRequiredBalance(coinError) }
  }

  if (debug?.type === 'ERROR_TYPE_NSFW') {
    return { type: 'nsfw' }
  }

  if (coinError?.code === 'COIN_ERROR_CODE_PAYMENT_REQUIRED') {
    return {
      type: 'payment-required',
      payUrl: coinError.paymentIntentResult?.stripeInvoicePayUrl ?? null,
      status: paymentStatus,
    }
  }

  const violations = getViolationDescriptions(debug)
  if (violations.some(description => description === 'upload_contains_minor')) {
    return { type: 'upload-minor' }
  }
  if (violations.some(description => PROMPT_MODERATION_DESCRIPTIONS.has(description))) {
    return { type: 'prompt-minor' }
  }

  return { type: 'generic' }
}

function mentionsAllAccess(error: unknown, debug: ReturnType<typeof getDebug>) {
  if (isAllAccessRequiredError(error)) {
    return true
  }
  return containsAllAccessText(debug)
}

function containsAllAccessText(value: unknown): boolean {
  if (typeof value === 'string') {
    return /all access/i.test(value)
  }
  if (Array.isArray(value)) {
    return value.some(containsAllAccessText)
  }
  if (value && typeof value === 'object') {
    return Object.values(value).some(containsAllAccessText)
  }
  return false
}

interface DebugDetails {
  type?: string
  coinError?: CoinErrorDetails
  fieldViolations?: unknown
}

function getDebug(error: unknown): DebugDetails | undefined {
  if (error instanceof FetchError) {
    return asDebug(error.details)
  }
  if (error instanceof ConnectError) {
    const detail = error.details[0]
    if (detail && typeof detail === 'object' && 'debug' in detail) {
      return asDebug(detail.debug)
    }
  }
  return undefined
}

function asDebug(value: unknown): DebugDetails | undefined {
  if (!value || typeof value !== 'object') {
    return undefined
  }
  return value as DebugDetails
}

function getCoinError(debug: ReturnType<typeof getDebug>) {
  const coinError = debug?.coinError
  if (!coinError || typeof coinError !== 'object') {
    return undefined
  }
  return coinError
}

function getRequiredBalance(coinError: CoinErrorDetails) {
  const requiredBalance = coinError.insufficientFundDetails?.requiredBalance
  if (requiredBalance === undefined || requiredBalance === null) {
    return null
  }
  const amount = Number(requiredBalance)
  return Number.isFinite(amount) ? amount : null
}

function getViolationDescriptions(debug: ReturnType<typeof getDebug>) {
  if (!Array.isArray(debug?.fieldViolations)) {
    return []
  }
  return debug.fieldViolations.flatMap(violation => {
    if (!violation || typeof violation !== 'object' || !('description' in violation)) {
      return []
    }
    const description = violation.description
    return typeof description === 'string' ? [description] : []
  })
}
